import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { Asset, AssetStatus } from "../__generated__/graphql";
import { GET_ASSET } from "../graphql/asset";

const POLL_INTERVAL_MS = 5000;

type AssetWaitingResult = {
    waiting: boolean;
    error: string | null;
    asset?: Asset | null;
    reset: () => void;
};

/**
 * Wait for the asset to be ready or error. While the asset is pending, polls
 * every 5 seconds; once a non-pending status arrives or an error happens,
 * polling is stopped via Apollo's `stopPolling`. `reset()` resumes polling.
 */
export const useAssetWaiting = (assetId?: string): AssetWaitingResult => {
    const { data, error: gqlError, refetch, startPolling, stopPolling } = useQuery(GET_ASSET, {
        fetchPolicy: "network-only",
        variables: { assetId: assetId || "" },
        pollInterval: POLL_INTERVAL_MS,
        skip: !assetId,
    });

    // The asset is "loaded" once its current status is non-pending — derived
    // directly from the latest query data, no local state mirror.
    const loadedAsset = useMemo(() => {
        const fetched = data?.getAsset || null;
        return fetched && fetched.status !== AssetStatus.Pending ? fetched : null;
    }, [data?.getAsset]);

    const error = useMemo(() => {
        if (!assetId) return "Asset ID is empty";
        if (gqlError) return gqlError.message || null;
        return null;
    }, [assetId, gqlError]);

    // Stop polling once we have a definitive answer; resume on reset / new id.
    useEffect(() => {
        if (loadedAsset || error) {
            stopPolling();
        } else if (assetId) {
            startPolling(POLL_INTERVAL_MS);
        }
    }, [assetId, loadedAsset, error, startPolling, stopPolling]);

    const reset = useCallback(() => {
        void refetch();
    }, [refetch]);

    const waiting = useMemo(() => {
        if (!assetId) return false;
        if (error) return false;
        return !loadedAsset;
    }, [assetId, error, loadedAsset]);

    return {
        waiting,
        error,
        asset: loadedAsset,
        reset,
    };
};
